import { Injectable } from '@angular/core';
import { describePrintDispatch } from './print-monitor';

/**
 * Webview-side writer for the install-folder `print.log` (R45).
 *
 * Sends one typed line over the `log_print_event` Tauri IPC command
 * (`{ printType, message }` → serde `PrintLogEntry`); the Rust shell owns the
 * file (exe-dir `print.log`, 2 MB rotation, app-log-dir fallback). Outside
 * the desktop build (no `window.__TAURI__`) every call is a no-op —
 * `console.debug` at most — so web/portal bundles are unaffected.
 */
@Injectable({ providedIn: 'root' })
export class PrintLogService {

    /** True in the Tauri desktop shell. Overridable in specs. */
    protected isTauri(): boolean {
        return typeof (window as any).__TAURI__ !== 'undefined';
    }

    /** Fire-and-forget one print.log line. Never throws. */
    log(printType: string, message: string): void {
        if (!this.isTauri()) {
            console.debug(`[print:${printType}] ${message}`);
            return;
        }
        this.send(printType, message);
    }

    /** Logs the UI side of a `handlePrintRequest` dispatch (invoice numbers etc.). */
    logDispatchRequest(data: any, duplicate: boolean): void {
        const entry = describePrintDispatch(data, duplicate);
        this.log(entry.printType, entry.message);
    }

    private async send(printType: string, message: string): Promise<void> {
        try {
            const tauri = await import('@tauri-apps/api/core');
            await tauri.invoke('log_print_event', { entry: { printType, message } });
        } catch {
            // Logging must never break printing — older shells without the
            // command (or a torn-down webview) simply drop the line.
        }
    }
}
